import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { Bot, Users, ScrollText, Lightbulb, MessageCircle, Sparkles, BarChart3, GraduationCap, MessagesSquare, BookOpen, Smartphone } from 'lucide-react';
import OverviewTab from './OverviewTab';
import ScriptTab from './ScriptTab';
import HistoricalTab from './HistoricalTab';
import ConversationsTab from './ConversationsTab';
import MirrorTab from './MirrorTab';
import SuggestionsTab from './SuggestionsTab';
import LearningTab from './LearningTab';
import TrainingTab from './TrainingTab';
import InstructionsTab from './InstructionsTab';
import ManagerWhatsAppAssistantTab from './ManagerWhatsAppAssistantTab';

type TabKey = 'manager_whatsapp' | 'overview' | 'instructions' | 'training' | 'learning' | 'script' | 'historical' | 'conversations' | 'mirror' | 'suggestions';

const TABS: Array<{ key: TabKey; label: string; icon: React.ElementType; description: string }> = [
  { key: 'manager_whatsapp', label: 'WhatsApp do Gestor', icon: Smartphone, description: 'Link de cadastro e retornos aos novos contatos' },
  { key: 'overview', label: 'Visão Geral', icon: BarChart3, description: 'Vendedores, orçamento e conversas ativas' },
  { key: 'instructions', label: 'Instruções', icon: BookOpen, description: 'Ensinar regras, requisitos e políticas ao agente' },
  { key: 'training', label: 'Simular', icon: MessagesSquare, description: 'Testar o agente como se fosse um lead' },
  { key: 'learning', label: 'Aprendizado', icon: GraduationCap, description: 'Importar histórico e ensinar o agente com aprovados e recusados' },
  { key: 'script', label: 'Script do Agente', icon: ScrollText, description: 'Perguntas, faixas de valor e objeções' },
  { key: 'historical', label: 'Análise Histórica', icon: Sparkles, description: 'Perfil de bom pagador a partir dos contratos' },
  { key: 'conversations', label: 'Conversas', icon: MessageCircle, description: 'O que o agente e a Lorena estão conversando' },
  { key: 'mirror', label: 'Espelho da Lorena', icon: Users, description: 'Frases e táticas aprendidas' },
  { key: 'suggestions', label: 'Sugestões', icon: Lightbulb, description: 'Melhorias no script propostas pelo agente' },
];

const LUBANK_UNIT_ID = '42c1cf1f-dab1-4233-bc61-d2d94b5cb0ca';

export default function AiAgent() {
  const { user } = useAuth();

  if (user && user.unit_id !== LUBANK_UNIT_ID && user.role !== 'admin') {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Bot className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600 text-lg font-medium">Funcionalidade indisponível</p>
          <p className="text-gray-500 text-sm mt-1">O Agente de IA ainda não está ativo para sua unidade.</p>
        </div>
      </div>
    );
  }

  const [activeTab, setActiveTab] = useState<TabKey>('overview');
  const [sellers, setSellers] = useState<Array<{ id: string; display_name: string; email: string }>>([]);
  const [selectedSellerId, setSelectedSellerId] = useState<string>('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    loadSellers();
  }, [user]);

  async function loadSellers() {
    if (!user) return;
    setLoading(true);
    let query = supabase
      .from('users')
      .select('id, display_name, email, role, unit_id')
      .eq('role', 'seller')
      .order('display_name');
    if (user.role === 'manager' && user.unit_id) {
      query = query.eq('unit_id', user.unit_id);
    }
    const { data, error } = await query;
    if (error) {
      console.error(error);
      setLoading(false);
      return;
    }
    const list = (data ?? []).map((u: any) => ({
      id: u.id,
      display_name: u.display_name || (u.email as string).split('@')[0],
      email: u.email,
    }));
    setSellers(list);
    if (list.length > 0 && !selectedSellerId) setSelectedSellerId(list[0].id);
    setLoading(false);
  }

  const selectedSeller = useMemo(
    () => sellers.find((s) => s.id === selectedSellerId) ?? null,
    [sellers, selectedSellerId]
  );

  if (!user || (user.role !== 'manager' && user.role !== 'admin')) {
    return (
      <div className="p-6">
        <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-slate-500">
          Este painel é exclusivo para gestores.
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <header className="mb-6">
          <div className="flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-4">
              <div className="rounded-xl bg-emerald-50 p-3">
                <Bot className="h-8 w-8 text-emerald-600" />
              </div>
              <div>
                <h1 className="text-2xl font-semibold text-slate-900">Agente de IA</h1>
                <p className="mt-1 text-sm text-slate-500">
                  Trabalha junto com a Lorena no WhatsApp, aprende com ela e sugere melhorias.
                </p>
              </div>
            </div>
            {activeTab !== 'manager_whatsapp' && <div className="w-full sm:w-72">
              <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
                Vendedor
              </label>
              <select
                value={selectedSellerId}
                onChange={(e) => setSelectedSellerId(e.target.value)}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                disabled={loading || sellers.length === 0}
              >
                {sellers.length === 0 && <option value="">Nenhum vendedor</option>}
                {sellers.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.display_name}
                  </option>
                ))}
              </select>
            </div>}
          </div>
        </header>

        <div className="mb-6 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-9">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={
                  'group flex flex-col items-start gap-2 rounded-xl border p-3 text-left transition ' +
                  (isActive
                    ? 'border-emerald-500 bg-emerald-50 shadow-sm ring-2 ring-emerald-200'
                    : 'border-slate-200 bg-white hover:border-emerald-200 hover:bg-emerald-50/40')
                }
              >
                <Icon className={'h-5 w-5 ' + (isActive ? 'text-emerald-600' : 'text-slate-500 group-hover:text-emerald-500')} />
                <div>
                  <div className={'text-sm font-medium ' + (isActive ? 'text-emerald-900' : 'text-slate-900')}>{tab.label}</div>
                  <div className="mt-0.5 text-[11px] leading-snug text-slate-500">{tab.description}</div>
                </div>
              </button>
            );
          })}
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          {loading && <div className="text-sm text-slate-500">Carregando...</div>}
          {!loading && activeTab === 'manager_whatsapp' && <ManagerWhatsAppAssistantTab unitId={user.unit_id ?? LUBANK_UNIT_ID} />}
          {!loading && activeTab !== 'manager_whatsapp' && !selectedSeller && (
            <div className="text-sm text-slate-500">Nenhum vendedor disponível para exibir.</div>
          )}
          {!loading && activeTab !== 'manager_whatsapp' && selectedSeller && (
            <>
              {activeTab === 'overview' && <OverviewTab sellerId={selectedSeller.id} sellerName={selectedSeller.display_name} />}
              {activeTab === 'instructions' && <InstructionsTab />}
              {activeTab === 'training' && <TrainingTab sellerId={selectedSeller.id} />}
              {activeTab === 'learning' && <LearningTab sellerId={selectedSeller.id} unitId={user.unit_id ?? null} />}
              {activeTab === 'script' && <ScriptTab sellerId={selectedSeller.id} />}
              {activeTab === 'historical' && <HistoricalTab sellerId={selectedSeller.id} unitId={user.unit_id ?? null} />}
              {activeTab === 'conversations' && <ConversationsTab sellerId={selectedSeller.id} />}
              {activeTab === 'mirror' && <MirrorTab sellerId={selectedSeller.id} />}
              {activeTab === 'suggestions' && <SuggestionsTab sellerId={selectedSeller.id} />}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
